import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import versionInfo from '../version.json';
import { environment } from '../Env_data/environment';
import { AppContext } from '../App';
import {
  Layers,
  GitBranch,
  GitCommit,
  Calendar,
  Clock,
  Server,
  User,
  Monitor,
  Copy,
  Check,
  ArrowLeft,
  Terminal,
  ShieldAlert,
  Code
} from 'lucide-react';

const WhoAmI = () => {
  const navigate = useNavigate();
  const { state } = useContext(AppContext) || { state: {} };
  const session = state?.session;
  const [copied, setCopied] = useState(false);
  const [viewJson, setViewJson] = useState(false);

  const clientInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
    online: navigator.onLine ? 'Online' : 'Offline',
    currentPath: window.location.pathname,
    origin: window.location.origin
  };

  const allInfo = {
    appName: environment.APP_NAME || 'spent_app',
    version: versionInfo.version,
    buildNumber: versionInfo.buildNumber,
    buildDate: versionInfo.buildDate,
    buildTimestamp: versionInfo.buildTimestamp,
    gitBranch: versionInfo.gitBranch,
    gitCommit: versionInfo.gitCommit,
    gitCommitMessage: versionInfo.gitCommitMessage,
    environment: versionInfo.environment || process.env.NODE_ENV,
    apiBaseUrl: environment.BASE_URL,
    authenticated: !!session?.id,
    user: session?.id ? { id: session.id, email: session.email, name: `${session.firstname || ''} ${session.lastname || ''}`.trim() } : null,
    client: clientInfo
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(allInfo, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 font-sans">
      <div className="w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center">
              <Terminal className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white">Route: /whoami</h1>
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  v{versionInfo.version}
                </span>
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  #{versionInfo.buildNumber}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Active build inspection & deployment diagnostic identity
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewJson(!viewJson)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-medium text-slate-200 border border-slate-700 transition"
            >
              <Code className="w-3.5 h-3.5 text-slate-400" />
              {viewJson ? 'Card View' : 'JSON View'}
            </button>
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-xs font-medium text-white transition shadow-sm"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy All'}
            </button>
          </div>
        </div>

        {/* Content Body */}
        {viewJson ? (
          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800/80 overflow-x-auto max-h-96">
            <pre className="text-xs font-mono text-emerald-400">
              {JSON.stringify(allInfo, null, 2)}
            </pre>
          </div>
        ) : (
          <div className="space-y-4">
            
            {/* Build Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800 flex items-start gap-3">
                <Layers className="w-5 h-5 text-indigo-400 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-slate-400 font-medium">Build Version & Number</div>
                  <div className="text-sm font-semibold text-white mt-0.5">
                    v{versionInfo.version} <span className="text-indigo-400 font-mono">(Build #{versionInfo.buildNumber})</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800 flex items-start gap-3">
                <Calendar className="w-5 h-5 text-emerald-400 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-slate-400 font-medium">Build Date & Time</div>
                  <div className="text-sm font-semibold text-white mt-0.5 font-mono">
                    {versionInfo.buildDate}
                  </div>
                </div>
              </div>

              <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800 flex items-start gap-3">
                <GitBranch className="w-5 h-5 text-amber-400 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-slate-400 font-medium">Git Branch</div>
                  <div className="text-sm font-semibold text-amber-300 mt-0.5 font-mono truncate">
                    {versionInfo.gitBranch}
                  </div>
                </div>
              </div>

              <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800 flex items-start gap-3">
                <GitCommit className="w-5 h-5 text-purple-400 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-slate-400 font-medium">Git Commit Hash</div>
                  <div className="text-sm font-semibold text-purple-300 mt-0.5 font-mono">
                    {versionInfo.gitCommit}
                  </div>
                </div>
              </div>
            </div>

            {/* Commit Message */}
            {versionInfo.gitCommitMessage && (
              <div className="bg-slate-950/60 p-3.5 rounded-2xl border border-slate-800 text-xs flex items-center gap-2">
                <span className="text-slate-500 font-medium whitespace-nowrap">Last Commit:</span>
                <span className="text-slate-300 font-mono truncate italic">"{versionInfo.gitCommitMessage}"</span>
              </div>
            )}

            {/* Server & Environment Info */}
            <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800 space-y-2.5">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-300 uppercase tracking-wider">
                <Server className="w-4 h-4 text-blue-400" />
                Environment & Backend API
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-slate-500">App Name: </span>
                  <span className="text-slate-200 font-mono">{environment.APP_NAME || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Environment: </span>
                  <span className="text-slate-200 font-mono capitalize">{versionInfo.environment}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-slate-500">API Base URL: </span>
                  <span className="text-indigo-300 font-mono break-all">{environment.BASE_URL || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* User Session State */}
            <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800 space-y-2 text-xs">
              <div className="flex items-center gap-2 font-semibold text-slate-300 uppercase tracking-wider">
                <User className="w-4 h-4 text-emerald-400" />
                Current User Session
              </div>
              {session?.id ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div><span className="text-slate-500">ID: </span><span className="text-white font-mono">{session.id}</span></div>
                  <div><span className="text-slate-500">Name: </span><span className="text-white">{session.firstname} {session.lastname}</span></div>
                  <div><span className="text-slate-500">Email: </span><span className="text-white truncate">{session.email}</span></div>
                </div>
              ) : (
                <div className="text-slate-500 italic">No user logged in (Guest Session)</div>
              )}
            </div>

            {/* Client Browser Diagnostics */}
            <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800 space-y-2 text-xs">
              <div className="flex items-center gap-2 font-semibold text-slate-300 uppercase tracking-wider">
                <Monitor className="w-4 h-4 text-slate-400" />
                Client Diagnostics
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-slate-400">
                <div>Status: <span className="text-emerald-400 font-medium">{clientInfo.online}</span></div>
                <div>Screen: <span className="text-slate-200 font-mono">{clientInfo.screen}</span></div>
                <div>Lang: <span className="text-slate-200">{clientInfo.language}</span></div>
              </div>
            </div>

          </div>
        )}

        {/* Footer Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-800">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={() => navigate(session?.id ? (session?.homes?.length ? '/home' : '/panel') : '/login')}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-medium text-slate-200 transition"
          >
            {session?.id ? 'Go to Dashboard' : 'Go to Login'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default WhoAmI;
